import {Component, OnInit} from '@angular/core';
import {StudentModel} from '../../../models/authentication/student.model';
import {ActivatedRoute, Router} from '@angular/router';
import {UserModel} from '../../../models/authentication/user.model';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AuthService} from '../../../services/auth.service';
import {TokenModel} from '../../../models/authentication/token.model';
import {SchoolModel} from '../../../models/school/school.model';
import {CultureModel} from '../../../models/localization-and-pricing/culture.model';
import {ModalService} from '../../../shared/modals/modal/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {ErrorModel} from '../../../models/shared/error.model';
import {TeacherInvitationModel} from '../../../models/authentication/teacher-invitation';
import {SchoolAdminTeacherInvitationService} from '../../../services/school/school-admin-teacher-invitation.service';
import {NavigationModel} from '../../../models/localization-and-pricing/navigation.model';
import {StringExtensionModel} from '../../../models/extensions/string-extension.model';
import {CountryModel} from '../../../models/localization-and-pricing/country.model';

@Component({
  selector: 'app-teacher-invitation-signup',
  templateUrl: './teacher-invitation-signup.component.html',
  styleUrls: ['./teacher-invitation-signup.component.css']
})
export class TeacherInvitationSignupComponent implements OnInit {

  public States = {
    SetupAccount: 'SetupAccount',
    // Current not used
    ExistingUser: 'ExistingUser',
    WrongAccount: 'WrongAccount',
    LinkExpired: 'LinkExpired',
    Error: 'Error'
  };

  state: string = undefined;

  token = null;
  payload: {
    _id: string,
    schoolId: string,
    culture: string,
    country: string,
    iat: number,
    exp: number
  } = null;

  user = new UserModel();
  teacherInvitation: TeacherInvitationModel;
  invitationError: ErrorModel = null;
  password = '';
  confirmPassword = '';

  public UserModel = UserModel;

  get pwLength(): boolean { return this.password.length >= 7; }
  get pwLetter(): boolean { return /[a-zA-Z]/.test(this.password); }
  get pwNumber(): boolean { return /[0-9]/.test(this.password); }
  get pwSymbol(): boolean { return /[^a-zA-Z0-9]/.test(this.password); }

  get canSubmit(): boolean {
    if (!this.user.firstName || this.user.firstName.length < 2) { return false; }
    if (!this.user.lastName || this.user.lastName.length < 2) { return false; }
    if (this.state === this.States.ExistingUser) { return true; }
    return this.pwLength && this.pwLetter && this.pwNumber && this.pwSymbol && this.password === this.confirmPassword;
  }

  constructor(private router: Router,
              private route: ActivatedRoute,
              private authSv: AuthService,
              private teacherInvitationSv: SchoolAdminTeacherInvitationService,
              private modalSv: ModalService,
              private tSv: TranslateService) {
  }

  ngOnInit() {
    this.fetchToken();
  }

  fetchToken() {
    this.route.params.subscribe((params) => {

      const helper = new JwtHelperService();
      this.token = params.token;

      if (!this.token) {
        // how an error as we are lacking a token
      } else {

        this.payload = <{
          _id: string,
          schoolId: string,
          culture: string,
          country: string,
          iat: number,
          exp: number
        }>helper.decodeToken(this.token);

        if (!CultureModel.getHomepageCulture()) {
          CultureModel.setHomepageCulture(this.payload.culture);
          this.tSv.use(CultureModel.getHomepageCulture());
        }

        if (TokenModel.isExpired(this.token)) {
          this.state = this.States.LinkExpired;
          return;
        }

        this.teacherInvitationSv.fetchInvitationTeacher(this.payload.schoolId, this.token).subscribe((teacherInvitation) => {

          this.teacherInvitation = teacherInvitation;
          this.updateInvitationState();

        }, (err: ErrorModel) => {

          // Handle error state logic with translated errors;
          this.invitationError = err;
          this.state = this.States.Error;

        });
      }
    });
  }

  updateInvitationState() {

    this.user.email = this.teacherInvitation.email;
    this.user.firstName = this.teacherInvitation.firstName;
    this.user.lastName = this.teacherInvitation.lastName;

    this.state = this.States.SetupAccount;

    const currentUser = UserModel.getCurrent();
    if (currentUser) {
      if (currentUser.email === this.user.email) {
        this.user = currentUser;
        this.state = this.States.ExistingUser;
      } else {
        this.state = this.States.WrongAccount;
      }
    }

  }

  onNavigateHome() {

    if (StudentModel.getCurrent()) {
      this.router.navigate(['platform', 'dash']);
    } else {
      this.router.navigate(['selectstudent']);
    }
  }

  getSchoolName(): string {

    if (this.teacherInvitation) {
      return this.teacherInvitation.schoolName;
    }

    return null;
  }

  getSchoolInitial(): string {

    if (this.getSchoolName()) {

      return this.getSchoolName().substring(0, 1).toUpperCase();
    }

    return null;
  }

  getSchoolCountry(): string {

    if (this.payload.country) {
      return CountryModel.title(this.payload.country);
    }

    return '';

  }

  getAccessType(): string {

    if (this.teacherInvitation) {
      return 'schoolAccessTypes.' + this.teacherInvitation.schoolAccessType;
    }

    return 'schoolAccessTypes.teacher';
  }

  onLogoutAndSwitchAccountClicked() {

    this.authSv.signOut().subscribe(() => {

    });

    UserModel.setCurrent(null);
    TokenModel.setCurrent(null);
    SchoolModel.setCurrent(null);
    StudentModel.setCurrent(null);

    this.updateInvitationState();

  }

  onTokenExpiredClicked() {

    if (CultureModel.getHomepageCulture()) {
      this.router.navigate(NavigationModel.getHomeURLPaths(['landing']));
    } else {
      this.router.navigate(['selectcountry']);
    }

  }

  onWrongTokenTypeClicked() {

    if (CultureModel.getHomepageCulture()) {
      this.router.navigate(NavigationModel.getHomeURLPaths(['landing']));
    } else {
      this.router.navigate(['selectcountry']);
    }

  }

  onSetupAccountAndAcceptInvitation() {

    if (!this.user.firstName || this.user.firstName.length < 2) {
      this.modalSv.showErrorModal(this.tSv.instant('reusable.error'),
        this.tSv.instant('schoolInvitation.firstNameRequiredError'));
      return;
    }

    if (!this.user.lastName || this.user.lastName.length < 2) {
      this.modalSv.showErrorModal(this.tSv.instant('reusable.error'),
        this.tSv.instant('schoolInvitation.lastNameRequiredError'));
      return;
    }

    if (this.state !== this.States.ExistingUser) {
      if (this.password !== this.confirmPassword) {
        this.modalSv.showErrorModal(this.tSv.instant('reusable.error'),
          this.tSv.instant('resetPassword.passwordsDoNotMatch'));
        return;
      }
    }

    // tslint:disable-next-line:max-line-length
    this.teacherInvitationSv.acceptTeacherInvitation(this.teacherInvitation.schoolId, this.token, this.password, this.user.firstName, this.user.lastName).subscribe((response) => {
      UserModel.setCurrent(response.user);
      TokenModel.setCurrent(response.tokens);
      SchoolModel.setCurrent(response.school);
      this.router.navigate(['selectstudent']);
    }, (err: ErrorModel) => {
      this.modalSv.showErrorModal(this.tSv.instant('reusable.error'), err.message);
    });
  }
}
